import { select, call, takeLatest, put } from 'redux-saga/effects';

import { InternalActions } from '../constants';
import { InternalApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteInternalAction,
    GetInternalAction,
    GetInternalsAction,
    PersistInternalAction,
} from '../actions/internal-actions';
import sortingService from '../../services/sorting';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';

import { groupInternal } from '../../helpers/internal/internal';
import { GetSeancesAction } from '../actions/seance-actions';
import { groupDataToCsv } from '../../helpers/internal/groupCsv';
import { exportToCsv } from '../../services/exports/csv';
import { toast } from 'react-toastify';
import _ from 'lodash';

function* handleFetchInternals(action: GetInternalsAction) {
    const oldData = yield select((state: StoreState) => state.internals.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: any = { ...filters };

    if (filterProps?.internalDate) {
        filterProps = {
            ...filters,
            ...filterProps?.internalDate,
        };
        delete filterProps.internalDate;
    }
    let sortObj = { ...sorting };
    if (_.isEmpty(sortObj)) {
        sortObj = { internalDate: 'desc' };
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        InternalApi.getInternals({
            ...filterProps,
            ...sortingService.makeOrderSymfony(sortObj),
            page,
            itemsPerPage: 100,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}
function* handleFetchInternalsCsv(action: GetInternalsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            InternalApi.getInternals({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage: 5000,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetInternalsAction = {
                    type: InternalActions.FETCH_INTERNALS_CSV,
                    payload: {
                        filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchInternalsCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupDataToCsv(dataAll);
                    exportToCsv('internals.csv', groupCsv);
                } else {
                    const groupCsvData = groupDataToCsv(data.payload);
                    exportToCsv('internals.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchInternal(action: GetInternalAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => InternalApi.getInternal(id));
}

function* handleDeleteInternal(action: DeleteInternalAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => InternalApi.deleteInternal(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistInternal(action: PersistInternalAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;
    const dataInternal = groupInternal(restData);
    yield resolveApiCall(
        action,
        () => (id ? InternalApi.updateInternal(id, dataInternal) : InternalApi.createInternal(dataInternal)),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);
            //if (!id) {}
            toast.success(`Internal was successfully ${!id ? 'created' : 'updated'}.`);
            if (restData.month) {
                const actionFiles: GetSeancesAction = {
                    type: InternalActions.FETCH_INTERNALS,
                    payload: {
                        filters: {
                            'provider.id': restData?.provider,
                            'createdAt[before]': restData.month.monthEnd,
                            'createdAt[after]': restData.month.monthStart,
                        },

                        itemsPerPage: 1200,
                        page: 1,
                    },
                };
                yield call(handleFetchInternals, actionFiles);
            } else {
                yield put(push(Router.generate(routes.INTERNALS_LIST)));
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (result.error && result.error.response && result.error.response.status) {
                    toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(InternalActions.FETCH_INTERNALS, handleFetchInternals);
    yield takeLatest(InternalActions.FETCH_INTERNALS_CSV, handleFetchInternalsCsv);
    yield takeLatest(InternalActions.FETCH_INTERNAL, handleFetchInternal);
    yield takeLatest(InternalActions.DELETE_INTERNAL, handleDeleteInternal);
    yield takeLatest(InternalActions.PERSIST_INTERNAL, handlePersistInternal);
}
